import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom"; // import useLocation
import { DollarSign, Calendar, Wrench, Database, FileText, Check, X, Clock } from "lucide-react";
import axios from "axios"; // Add axios import
import { decryptId } from "../../utils/crypto";

function ProjectDetails({userId}) { 
  const { id } = useParams();
  const location = useLocation(); // get location object
  const notify = location.state?.notify; // get notify from navigation state
  const decryptedId = decryptId(id);
  const [project, setProject] = useState(null);
  const [professionalSkills, setProfessionalSkills] = useState([]);
  const [professionalProjectTypes, setProfessionalProjectTypes] = useState([]); // Added state for professional project types
  const [professionalId, setProfessionalId] = useState(null); // Added state for professional ID

  useEffect(() => {
    console.log('User ID:', userId);
    console.log('Decrypted ID:', decryptedId);
    fetchProjectDetails();
    fetchProfessionalSkills();
    fetchProfessionalProjectTypes(); // Fetch professional project types when component mounts
  }, []);

  const fetchProfessionalProjectTypes = async () => {
    try {
      const response = await fetch(`http://localhost:5000/professional-projects/${userId}`);
      const data = await response.json();
      const projectTypes = data.map(types => types.type);
      const professionalId = data.length > 0 ? data[0].professional_id : null; // Store the first professional_id
      setProfessionalId(professionalId); // Set the professional ID state
      setProfessionalProjectTypes(projectTypes);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const fetchProfessionalSkills = async () => {
    try {
      const response = await fetch(`http://localhost:5000/professional-skills?userId=${userId}`)
      const data = await response.json()
      const skills = data.map(skill => skill.skill_name)
      setProfessionalSkills(skills)
    } catch (error) {
      console.error('Error fetching professional skills:', error)
    }
  }

  const fetchProjectDetails = async () => {
    try {
      const response = await fetch(`http://localhost:5000/client-project/${decryptedId}`);
      const data = await response.json();
      setProject(data);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const handleAcceptProject = async () => {
    try {
      const response = await fetch('http://localhost:5000/map-project', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientProjectId: decryptedId,
          professionalId: professionalId,
          status: notify ? 'accepted' : 'requested', // set status based on notify
        }),
      });
      if (response.ok) {
        alert('Project accepted successfully!');
        // Insert notification for client
        if (project && project.user_id && project.client_project_id) {
          try {
            await axios.post("http://localhost:5000/notifications", {
              user_id: project.user_id,
              client_project_id: project.client_project_id,
              type: "link",
              message: "A professional has requested to participate in your project.",
            });
          } catch (notifErr) {
            // Optionally handle notification error
            console.error("Notification error:", notifErr);
          }
        }
      } else {
        alert('You already sent request.');
      }
    } catch (error) {
      console.error('Error accepting project:', error);
      alert('Error accepting project.');
    }
  };

  if (!project) {
    return <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px 0', fontSize: '1.1rem' }}>Loading...</div>;
  }

  const calculateMatchPercentage = (projectSkills, professionalSkills, projectType, professionalProjectTypes) => {
    if (!projectSkills || !professionalSkills) return 0;
    const projectSkillsArray = projectSkills.split(',').map(skill => skill.trim().toLowerCase());
    const professionalSkillsArray = professionalSkills.map(skill => skill.toLowerCase());
    const matchingSkills = projectSkillsArray.filter(skill => professionalSkillsArray.includes(skill));
    let matchPercentage = (matchingSkills.length / projectSkillsArray.length) * 100;
    
    if (professionalProjectTypes.includes(projectType)) {
      matchPercentage += 10;
    } else {
      matchPercentage -= 10;
    }
    if (matchPercentage > 100) {
      matchPercentage = 100;
    }
  
    return matchPercentage;
  };

  const matchPercentage = calculateMatchPercentage(project.skills, professionalSkills, project.type, professionalProjectTypes);
  const scoreColor = matchPercentage >= 75 ? '#10b981' : matchPercentage >= 50 ? '#f59e0b' : '#ef4444';
  const projectSkillsArray = project.skills ? project.skills.split(',').map(skill => skill.trim()) : [];
  const matchingSkills = projectSkillsArray.filter(skill => professionalSkills.includes(skill));
  const missingSkills = projectSkillsArray.filter(skill => !professionalSkills.includes(skill));

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div style={{ position: 'relative', width: '96px', height: '96px' }}>
          <svg style={{ width: '96px', height: '96px', transform: 'rotate(-90deg)' }} viewBox="0 0 100 100">
            <circle stroke="var(--border-subtle)" strokeWidth="10" cx="50" cy="50" r="40" fill="transparent" />
            <circle stroke={scoreColor} strokeWidth="10" cx="50" cy="50" r="40" fill="transparent"
              strokeDasharray="251.2" strokeDashoffset={(1 - matchPercentage / 100) * 251.2}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '1.3rem', fontWeight: 700, color: scoreColor }}>{matchPercentage.toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h2 style={{ fontSize: '1.8rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '12px' }}>{project.title}</h2>
          <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '28px', lineHeight: 1.7 }}>
            {project.description}
          </p>

          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', marginBottom: '28px', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, var(--accent-cyan))' }} />
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '18px' }}>Project Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DetailItem icon={DollarSign} title="Budget" content={project.budget_range} />
              <DetailItem icon={Calendar} title="Deadline" content={new Date(project.deadline).toLocaleDateString()} />
              <DetailItem icon={Database} title="Required Technologies" content={project.skills ? project.skills.split(',').join(', ') : 'No skills specified'} />
              <DetailItem icon={FileText} title="Project Type" content={project.type} />
            </div>
          </div>

          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>Client's Additional Notes</h3>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>{project.note}</p>
          </div>
        </div>

        <div>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', marginBottom: '28px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '18px' }}>Mapping Details</h3>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981' }}>
                <Check size={18} /> Matching Skills
              </h4>
              <p style={{ color: 'var(--text-secondary)' }}>{matchingSkills.length > 0 ? matchingSkills.join(', ') : 'No matching skills found'}</p>
            </div>
            <div>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}>
                <X size={18} /> Missing Skills
              </h4>
              <p style={{ color: 'var(--text-secondary)' }}>{missingSkills.length > 0 ? missingSkills.join(', ') : 'No missing skills found'}</p>
            </div>
          </div>
          <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border-glass)', padding: '28px', marginBottom: '28px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '18px' }}>Business Details</h3>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)' }}>{project.client_name}</p>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{project.client_description}</p>
            </div>
            <div>
              <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--accent-cyan)' }}>
                <Clock size={18} /> Years in Market
              </h4>
              <p style={{ color: 'var(--text-secondary)' }}>{project.client_years_in_market}</p>
            </div>
          </div>

          <div className="space-y-4">
            <button
              style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', fontWeight: 700, padding: '14px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '1rem', transition: 'opacity 0.2s' }}
              onClick={handleAcceptProject}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Request Participation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ icon: Icon, title, content }) {
  return (
    <div>
      <h4 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
        <Icon style={{ color: 'var(--accent-cyan)' }} size={18} /> {title}
      </h4>
      <p style={{ color: 'var(--text-secondary)' }}>{content}</p>
    </div>
  );
}

export default ProjectDetails


